import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// ─── Helpers ────────────────────────────────────────────────────────────────

const getCurrentPeriod = () => {
  const now = new Date();
  const y = now?.getFullYear();
  const m = String(now?.getMonth() + 1)?.padStart(2, '0');
  return `${y}-${m}`;
};

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '$0.00';
  return '$' + Number(val)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SERVERS = ['MENA', 'FR', 'IT', 'DE'];

// Calculate bonusManager for a single creator row
const calcBonusManager = (total_bonus, charge, incremental_bonus, rate) => {
  const b = total_bonus || 0;
  const c = charge || 0;
  const inc = incremental_bonus || 0;
  const r = rate || 50;
  return (b - inc - c) / 2 + (inc * r / 100);
};

// ─── Main Component ──────────────────────────────────────────────────────────

const AdminFinance = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const topRef = useRef(null);

  const currentPeriod = getCurrentPeriod();

  // Filters
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);
  const [selectedServer, setSelectedServer] = useState('ALL');
  const [selectedManager, setSelectedManager] = useState('ALL');

  // Data
  const [monthlyData, setMonthlyData] = useState([]);
  const [managers, setManagers] = useState([]);
  const [rateMap, setRateMap] = useState({});
  const [managerNameMap, setManagerNameMap] = useState({});
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);

  // Section 5 data (from RPC)
  const [financeByPeriod, setFinanceByPeriod] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Auth
  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session?.user?.email);
    });
  }, []);

  // Fetch available periods with RPC
  useEffect(() => {
    const fetchPeriods = async () => {
      const { data: periodsData, error } = await supabase?.rpc('get_distinct_periods');
      const uniquePeriods = periodsData?.map(p => p?.period) || [];
      setPeriods(uniquePeriods);
      setSelectedPeriod(uniquePeriods?.[0] || '2026-03');
    };
    fetchPeriods();
  }, []);

  // Fetch managers
  useEffect(() => {
    const fetchManagers = async () => {
      const { data } = await supabase?.from('users')?.select('id, name')?.in('role', ['manager', 'admin', 'region_manager'])?.order('name');
      setManagers(data || []);
    };
    fetchManagers();
  }, []);

  // FETCH 1 — Monthly aggregates via RPC for chart and summary table
  useEffect(() => {
    const fetchFinanceByPeriod = async () => {
      setSummaryLoading(true);
      setChartLoading(true);
      try {
        const { data, error } = await supabase?.rpc('get_finance_by_period');
        if (error) {
          console.error('get_finance_by_period error:', error);
          setFinanceByPeriod([]);
          setChartData([]);
          return;
        }

        const rows = data || [];
        setFinanceByPeriod(rows);

        // Build chart data: ALL periods from RPC, sorted ascending, no year filter
        const sorted = [...rows]?.sort((a, b) => a?.period?.localeCompare(b?.period));
        const chart = sorted?.map(row => {
          let bonusGenere = row?.bonus_genere || 0;
          let charge = row?.total_charge || 0;
          const incremental = row?.total_incremental || 0;
          let commission = ((bonusGenere - incremental - charge) / 2) + (incremental * 0.50);
          const resultatNet = bonusGenere - charge - commission;
          return {
            period: row?.period,
            bonusGenere: Math.round(bonusGenere * 100) / 100,
            charge: Math.round(charge * 100) / 100,
            commissionManagers: Math.round(commission * 100) / 100,
            resultatNet: Math.round(resultatNet * 100) / 100,
          };
        });
        setChartData(chart);
      } catch (err) {
        console.error('Finance by period fetch error:', err);
        setFinanceByPeriod([]);
        setChartData([]);
      } finally {
        setSummaryLoading(false);
        setChartLoading(false);
      }
    };
    fetchFinanceByPeriod();
  }, []);

  // FETCH 2 — Selected period detail for KPI cards, server table, manager table
  const fetchMainData = useCallback(async (period) => {
    setLoading(true);
    try {
      // Fetch creator_monthly_history for selected period with limit(5000)
      const { data: monthlyRows, error } = await supabase?.from('creator_monthly_history')?.select('total_bonus, charge, incremental_bonus, manager_id, server')?.eq('period', period)?.limit(5000);

      if (error) console.error('creator_monthly_history fetch error:', error);

      // Fetch manager_rates for period
      const { data: rates } = await supabase?.from('manager_rates')?.select('manager_id, incremental_rate')?.eq('period', period);

      const newRateMap = {};
      rates?.forEach(r => { newRateMap[r.manager_id] = r?.incremental_rate || 50; });

      // Fetch manager names for all roles
      const { data: managersData } = await supabase?.from('users')?.select('id, name')?.in('role', ['manager', 'admin', 'region_manager']);

      const managerNameMap = {};
      managersData?.forEach(m => {
        if (m?.id) managerNameMap[m.id] = m?.name;
      });

      setManagerNameMap(managerNameMap);
      setMonthlyData(monthlyRows || []);
      setRateMap(newRateMap);
    } catch (err) {
      console.error('Finance main data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPeriod) fetchMainData(selectedPeriod);
  }, [selectedPeriod, fetchMainData]);

  // ─── Filtered data ──────────────────────────────────────────────────────────

  const filteredData = useMemo(() => {
    let data = monthlyData;
    if (selectedServer !== 'ALL') data = data?.filter(r => r?.server === selectedServer);
    if (selectedManager !== 'ALL') data = data?.filter(r => r?.manager_id === selectedManager);
    return data;
  }, [monthlyData, selectedServer, selectedManager]);

  // ─── KPI Calculations ───────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    let totalBonusGenere = 0;
    let totalCharge = 0;
    let totalCommissionManagers = 0;

    filteredData?.forEach(r => {
      const rate = rateMap?.[r?.manager_id] || 50;
      totalBonusGenere += r?.total_bonus || 0;
      totalCharge += r?.charge || 0;
      totalCommissionManagers += calcBonusManager(r?.total_bonus, r?.charge, r?.incremental_bonus, rate);
    });

    const resultatNetAgence = totalBonusGenere - totalCharge - totalCommissionManagers;

    return {
      totalBonusGenere,
      totalCharge,
      totalCommissionManagers,
      resultatNetAgence,
    };
  }, [filteredData, rateMap]);

  // ─── Server breakdown ───────────────────────────────────────────────────────

  const serverBreakdown = useMemo(() => {
    const rows = SERVERS?.map(server => {
      const serverData = filteredData?.filter(r => r?.server === server);
      let bonusGenere = 0, charge = 0, commission = 0;
      serverData?.forEach(r => {
        const rate = rateMap?.[r?.manager_id] || 50;
        bonusGenere += r?.total_bonus || 0;
        charge += r?.charge || 0;
        commission += calcBonusManager(r?.total_bonus, r?.charge, r?.incremental_bonus, rate);
      });
      return { server, bonusGenere, charge, commission, resultatNet: bonusGenere - charge - commission };
    });

    const total = rows?.reduce(
      (acc, r) => ({
        server: 'TOTAL',
        bonusGenere: acc?.bonusGenere + r?.bonusGenere,
        charge: acc?.charge + r?.charge,
        commission: acc?.commission + r?.commission,
        resultatNet: acc?.resultatNet + r?.resultatNet,
      }),
      { server: 'TOTAL', bonusGenere: 0, charge: 0, commission: 0, resultatNet: 0 }
    );

    return [...rows, total];
  }, [filteredData, rateMap]);

  // ─── Manager breakdown ──────────────────────────────────────────────────────

  const managerBreakdown = useMemo(() => {
    const managerMap = {};
    filteredData?.forEach(r => {
      const mid = r?.manager_id;
      if (!mid) return;
      if (!managerMap?.[mid]) managerMap[mid] = { bonusGenere: 0, charge: 0, commission: 0 };
      const rate = rateMap?.[mid] || 50;
      managerMap[mid].bonusGenere += r?.total_bonus || 0;
      managerMap[mid].charge += r?.charge || 0;
      managerMap[mid].commission += calcBonusManager(r?.total_bonus, r?.charge, r?.incremental_bonus, rate);
    });

    const rows = Object.entries(managerMap)?.map(([mid, vals]) => {
      const rate = rateMap?.[mid] || 50;
      const managerName = managerNameMap?.[mid] ||
                          managerNameMap?.[mid?.toLowerCase()] ||
                          managers?.find(m => m?.id === mid)?.name ||
                          'Manager inconnu';
      return {
        managerId: mid,
        managerName,
        bonusGenere: vals?.bonusGenere,
        charge: vals?.charge,
        rate,
        commission: vals?.commission,
        resultatNet: vals?.bonusGenere - vals?.charge - vals?.commission,
      };
    });

    rows?.sort((a, b) => b?.commission - a?.commission);

    const total = rows?.reduce(
      (acc, r) => ({
        managerId: '__total__',
        managerName: 'TOTAL',
        bonusGenere: acc?.bonusGenere + r?.bonusGenere,
        charge: acc?.charge + r?.charge,
        rate: null,
        commission: acc?.commission + r?.commission,
        resultatNet: acc?.resultatNet + r?.resultatNet,
      }),
      { managerId: '__total__', managerName: 'TOTAL', bonusGenere: 0, charge: 0, rate: null, commission: 0, resultatNet: 0 }
    );

    return [...rows, total];
  }, [filteredData, rateMap, managers, managerNameMap]);

  // ─── Section 5: Monthly summary from RPC data ──────────────────────────────

  const monthlySummary = useMemo(() => {
    return financeByPeriod?.map(row => {
      let bonusGenere = row?.bonus_genere || 0;
      let charge = row?.total_charge || 0;
      const incremental = row?.total_incremental || 0;
      let commission = ((bonusGenere - incremental - charge) / 2) + (incremental * 0.50);
      const resultatNet = bonusGenere - charge - commission;
      return {
        period: row?.period,
        bonusGenere: Math.round(bonusGenere * 100) / 100,
        charge: Math.round(charge * 100) / 100,
        commission: Math.round(commission * 100) / 100,
        resultatNet: Math.round(resultatNet * 100) / 100,
      };
    });
    // financeByPeriod is already sorted DESC by period from RPC
  }, [financeByPeriod]);

  const monthlySummaryTotal = useMemo(() => {
    return monthlySummary?.reduce(
      (acc, r) => ({
        period: 'TOTAL',
        bonusGenere: acc?.bonusGenere + r?.bonusGenere,
        charge: acc?.charge + r?.charge,
        commission: acc?.commission + r?.commission,
        resultatNet: acc?.resultatNet + r?.resultatNet,
      }),
      { period: 'TOTAL', bonusGenere: 0, charge: 0, commission: 0, resultatNet: 0 }
    );
  }, [monthlySummary]);

  const isCurrentPeriod = selectedPeriod === currentPeriod;

  // ─── Custom Tooltip ─────────────────────────────────────────────────────────

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 8, padding: '10px 14px' }}>
        <p style={{ color: '#fff', fontWeight: 600, marginBottom: 6 }}>{label}</p>
        {payload?.map((entry, i) => (
          <p key={i} style={{ color: entry?.color, fontSize: 13, margin: '2px 0' }}>
            {entry?.name}: {formatCurrency(entry?.value)}
          </p>
        ))}
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div ref={topRef} className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: '#0a0a0a', fontFamily: 'Inter, sans-serif' }}>
      <div className="hidden md:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(prev => !prev)}
          userEmail={userEmail}
        />
      </div>
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.finance.title', 'Vue Financière')} />
        <div className="p-6 max-w-screen-2xl mx-auto">

          {/* ── Header ── */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                {t('admin.finance.title', 'Vue Financière')}
              </h1>
              <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {t('admin.finance.subtitle', 'Analyse financière par période, serveur et manager')}
              </p>
            </div>

            <div className="ml-auto flex flex-wrap items-center gap-3">
              {/* Period selector */}
              <div className="flex flex-col gap-1">
                <label className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('admin.finance.period', 'Période')}</label>
                <select
                  value={selectedPeriod}
                  onChange={e => setSelectedPeriod(e?.target?.value)}
                  className="rounded-lg px-3 py-2 text-base text-white border outline-none"
                  style={{ backgroundColor: '#1a1a1a', borderColor: '#333', minWidth: 120 }}
                >
                  {periods?.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                  {periods?.length === 0 && (
                    <option value={currentPeriod}>{currentPeriod}</option>
                  )}
                </select>
              </div>

              {/* Server filter */}
              <div className="flex flex-col gap-1">
                <label className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('admin.finance.server', 'Serveur')}</label>
                <select
                  value={selectedServer}
                  onChange={e => setSelectedServer(e?.target?.value)}
                  className="rounded-lg px-3 py-2 text-base text-white border outline-none"
                  style={{ backgroundColor: '#1a1a1a', borderColor: '#333', minWidth: 100 }}
                >
                  <option value="ALL">ALL</option>
                  {SERVERS?.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {/* Manager filter */}
              <div className="flex flex-col gap-1">
                <label className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('admin.finance.manager', 'Manager')}</label>
                <select
                  value={selectedManager}
                  onChange={e => setSelectedManager(e?.target?.value)}
                  className="rounded-lg px-3 py-2 text-base text-white border outline-none"
                  style={{ backgroundColor: '#1a1a1a', borderColor: '#333', minWidth: 140 }}
                >
                  <option value="ALL">ALL</option>
                  {managers?.map(m => (
                    <option key={m?.id} value={m?.id}>{m?.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ── Partial data notice ── */}
          {isCurrentPeriod && (
            <div
              className="flex items-center gap-2 rounded-lg px-4 py-3 mb-5 text-sm"
              style={{ backgroundColor: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', color: '#fbbf24' }}
            >
              ⚠️ {t('admin.finance.currentMonthWarning', 'Mois en cours — données partielles, charge non finalisée')}
            </div>
          )}

          {/* ── Section 1: KPI Cards ── */}
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[1,2,3,4]?.map(i => (
                <div key={i} className="rounded-xl p-5 animate-pulse" style={{ backgroundColor: '#141414', height: 110 }} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Card 1: Bonus Généré */}
              <div className="rounded-xl p-5" style={{ backgroundColor: '#141414', border: '1px solid #1f1f1f' }}>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{t('admin.finance.bonusGenerated')}</p>
                <p className="text-2xl font-bold" style={{ color: '#22c55e', fontFamily: 'Sora, sans-serif' }}>
                  {formatCurrency(kpis?.totalBonusGenere)}
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('admin.finance.totalGrossPeriod')}</p>
              </div>

              {/* Card 2: Charge */}
              <div className="rounded-xl p-5" style={{ backgroundColor: '#141414', border: '1px solid #1f1f1f' }}>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{t('admin.finance.charge')}</p>
                <p className="text-2xl font-bold" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>
                  {formatCurrency(kpis?.totalCharge)}
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('admin.finance.totalCharges')}</p>
              </div>

              {/* Card 3: Commission Managers */}
              <div className="rounded-xl p-5" style={{ backgroundColor: '#141414', border: '1px solid #1f1f1f' }}>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{t('admin.finance.commissionManagers')}</p>
                <p className="text-2xl font-bold" style={{ color: '#f97316', fontFamily: 'Sora, sans-serif' }}>
                  {formatCurrency(kpis?.totalCommissionManagers)}
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('admin.finance.totalCommissions')}</p>
              </div>

              {/* Card 4: Résultat Net Agence */}
              <div className="rounded-xl p-5" style={{ backgroundColor: '#141414', border: '1px solid #1f1f1f' }}>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{t('admin.finance.netResultAgency')}</p>
                <p
                  className="text-2xl font-bold"
                  style={{ color: kpis?.resultatNetAgence >= 0 ? '#22c55e' : '#ef4444', fontFamily: 'Sora, sans-serif' }}
                >
                  {formatCurrency(kpis?.resultatNetAgence)}
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('admin.finance.afterChargesCommissions')}</p>
              </div>
            </div>
          )}

          {/* ── Section 2: Line Chart (from RPC data, all periods) ── */}
          <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: '#141414', border: '1px solid #1f1f1f' }}>
            <h2 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.finance.chartMonthlyEvolution')}
            </h2>
            {chartLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
              </div>
            ) : chartData?.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                {t('admin.finance.noDataAvailable')}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                  <XAxis
                    dataKey="period"
                    tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                    axisLine={{ stroke: '#333' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                    axisLine={{ stroke: '#333' }}
                    tickLine={false}
                    tickFormatter={v => `$${(v / 1000)?.toFixed(0)}k`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, paddingTop: 12 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="bonusGenere"
                    name={t('admin.finance.bonusGeneratedCol')}
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#22c55e' }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="charge"
                    name={t('admin.finance.chargeCol')}
                    stroke="#f97316"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#f97316' }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="commissionManagers"
                    name={t('admin.finance.commissionCol')}
                    stroke="#eab308"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#eab308' }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="resultatNet"
                    name={t('admin.finance.netResultCol')}
                    stroke="#ef4444"
                    strokeWidth={2.5}
                    strokeDasharray="5 3"
                    dot={{ r: 3, fill: '#ef4444' }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* ── Section 3: Détail par Serveur ── */}
          <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: '#141414', border: '1px solid #1f1f1f' }}>
            <h2 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.finance.detailByServer')}
            </h2>
            {loading ? (
              <div className="animate-pulse h-32 rounded-lg" style={{ backgroundColor: '#1a1a1a' }} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                      {[t('admin.finance.serverCol'), t('admin.finance.bonusGeneratedCol'), t('admin.finance.chargeCol'), t('admin.finance.commissionCol'), t('admin.finance.netResultCol')]?.map(col => (
                        <th
                          key={col}
                          className="text-left py-2.5 px-3 font-medium"
                          style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {serverBreakdown?.map((row, i) => {
                      const isTotal = row?.server === 'TOTAL';
                      return (
                        <tr
                          key={row?.server}
                          style={{
                            borderBottom: i < serverBreakdown?.length - 1 ? '1px solid #1a1a1a' : 'none',
                            backgroundColor: isTotal ? 'rgba(255,255,255,0.03)' : 'transparent',
                          }}
                        >
                          <td className="py-2.5 px-3 font-semibold" style={{ color: isTotal ? '#fff' : 'rgba(255,255,255,0.8)' }}>
                            {row?.server}
                          </td>
                          <td className="py-2.5 px-3" style={{ color: '#22c55e' }}>
                            {formatCurrency(row?.bonusGenere)}
                          </td>
                          <td className="py-2.5 px-3" style={{ color: '#f97316' }}>
                            {formatCurrency(row?.charge)}
                          </td>
                          <td className="py-2.5 px-3" style={{ color: '#eab308' }}>
                            {formatCurrency(row?.commission)}
                          </td>
                          <td className="py-2.5 px-3 font-semibold" style={{ color: row?.resultatNet >= 0 ? '#22c55e' : '#ef4444' }}>
                            {formatCurrency(row?.resultatNet)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Section 4: Détail par Manager ── */}
          <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: '#141414', border: '1px solid #1f1f1f' }}>
            <h2 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.finance.detailByManager')}
            </h2>
            {loading ? (
              <div className="animate-pulse h-40 rounded-lg" style={{ backgroundColor: '#1a1a1a' }} />
            ) : managerBreakdown?.length <= 1 ? (
              <p className="text-sm py-6 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>
                {t('admin.finance.noDataAvailable')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                      {[t('admin.finance.managerCol'), t('admin.finance.bonusGeneratedCol'), t('admin.finance.chargeCol'), t('admin.finance.rateCol'), t('admin.finance.commissionCol'), t('admin.finance.netResultAgency')]?.map(col => (
                        <th
                          key={col}
                          className="text-left py-2.5 px-3 font-medium"
                          style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {managerBreakdown?.map((row, i) => {
                      const isTotal = row?.managerId === '__total__';
                      return (
                        <tr
                          key={row?.managerId}
                          style={{
                            borderBottom: i < managerBreakdown?.length - 1 ? '1px solid #1a1a1a' : 'none',
                            backgroundColor: isTotal ? 'rgba(255,255,255,0.03)' : 'transparent',
                          }}
                        >
                          <td className="py-2.5 px-3 font-semibold" style={{ color: isTotal ? '#fff' : 'rgba(255,255,255,0.85)' }}>
                            {row?.managerName}
                          </td>
                          <td className="py-2.5 px-3" style={{ color: '#22c55e' }}>
                            {formatCurrency(row?.bonusGenere)}
                          </td>
                          <td className="py-2.5 px-3" style={{ color: '#f97316' }}>
                            {formatCurrency(row?.charge)}
                          </td>
                          <td className="py-2.5 px-3" style={{ color: 'rgba(255,255,255,0.6)' }}>
                            {row?.rate !== null ? `${row?.rate}%` : '—'}
                          </td>
                          <td className="py-2.5 px-3" style={{ color: '#eab308' }}>
                            {formatCurrency(row?.commission)}
                          </td>
                          <td className="py-2.5 px-3 font-semibold" style={{ color: row?.resultatNet >= 0 ? '#22c55e' : '#ef4444' }}>
                            {formatCurrency(row?.resultatNet)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Section 5: Récapitulatif par Mois (from RPC) ── */}
          <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: '#141414', border: '1px solid #1f1f1f' }}>
            <h2 className="text-base font-semibold text-white mb-4" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.finance.monthlySummary')}
            </h2>
            {summaryLoading ? (
              <div className="animate-pulse h-48 rounded-lg" style={{ backgroundColor: '#1a1a1a' }} />
            ) : monthlySummary?.length === 0 ? (
              <p className="text-sm py-6 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>
                {t('admin.finance.noDataAvailable')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                      {[t('admin.finance.periodCol'), t('admin.finance.bonusGeneratedCol'), t('admin.finance.chargeCol'), t('admin.finance.commissionManagers'), t('admin.finance.netResultAgency')]?.map(col => (
                        <th
                          key={col}
                          className="text-left py-2.5 px-3 font-medium"
                          style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {monthlySummary?.map((row, i) => {
                      const isSelected = row?.period === selectedPeriod;
                      return (
                        <tr
                          key={row?.period}
                          onClick={() => {
                            setSelectedPeriod(row?.period);
                            topRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }}
                          style={{
                            borderBottom: i < monthlySummary?.length - 1 ? '1px solid #1a1a1a' : 'none',
                            backgroundColor: isSelected ? 'rgba(239,68,68,0.06)' : 'transparent',
                            border: isSelected ? '1px solid rgba(239,68,68,0.5)' : undefined,
                            cursor: 'pointer',
                            transition: 'background-color 0.15s',
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <td className="py-2.5 px-3 font-semibold" style={{ color: isSelected ? '#ef4444' : 'rgba(255,255,255,0.85)' }}>
                            {row?.period}
                          </td>
                          <td className="py-2.5 px-3" style={{ color: '#22c55e' }}>
                            {formatCurrency(row?.bonusGenere)}
                          </td>
                          <td className="py-2.5 px-3" style={{ color: '#f97316' }}>
                            {formatCurrency(row?.charge)}
                          </td>
                          <td className="py-2.5 px-3" style={{ color: '#eab308' }}>
                            {formatCurrency(row?.commission)}
                          </td>
                          <td className="py-2.5 px-3 font-semibold" style={{ color: row?.resultatNet >= 0 ? '#22c55e' : '#ef4444' }}>
                            {formatCurrency(row?.resultatNet)}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Total row */}
                    <tr style={{ borderTop: '1px solid #333', backgroundColor: 'rgba(255,255,255,0.03)' }}>
                      <td className="py-2.5 px-3 font-bold" style={{ color: '#fff' }}>TOTAL</td>
                      <td className="py-2.5 px-3 font-bold" style={{ color: '#22c55e' }}>
                        {formatCurrency(monthlySummaryTotal?.bonusGenere)}
                      </td>
                      <td className="py-2.5 px-3 font-bold" style={{ color: '#f97316' }}>
                        {formatCurrency(monthlySummaryTotal?.charge)}
                      </td>
                      <td className="py-2.5 px-3 font-bold" style={{ color: '#eab308' }}>
                        {formatCurrency(monthlySummaryTotal?.commission)}
                      </td>
                      <td className="py-2.5 px-3 font-bold" style={{ color: monthlySummaryTotal?.resultatNet >= 0 ? '#22c55e' : '#ef4444' }}>
                        {formatCurrency(monthlySummaryTotal?.resultatNet)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </main>
      <AdminBottomNav />
    </div>
  );
};

export default AdminFinance;